<template>
  <section class="vp-card">
    <div class="vp-card-head">
      <h6 class="vp-card-title">
        <i class="ph-duotone ph-article"></i>{{ $t('profile.about.title') }}
      </h6>
      <button
        v-if="canEditTenantInfo && !editing"
        type="button"
        class="vp-btn vp-btn-ghost vp-btn-sm"
        :disabled="isLoading"
        @click="startEditing"
      >
        <i class="ph-duotone ph-pencil-simple"></i>{{ $t('common.edit') }}
      </button>
    </div>
    <div class="vp-card-body">
      <div v-if="isLoading" class="vp-loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') }}</span>
        </span>
      </div>
      <template v-else>
        <!-- View mode -->
        <div v-if="!editing" class="about-content">
          <p v-if="about" class="about-text" v-html="formatAbout(about)"></p>
          <p v-else class="about-text about-text--empty">{{ $t('profile.about.noDescription') }}</p>
        </div>

        <!-- Edit mode -->
        <div v-else>
          <textarea
            v-model="editAbout"
            class="vp-control about-textarea"
            rows="5"
            :aria-label="$t('profile.about.title')"
            :placeholder="$t('profile.about.placeholder')"
            :disabled="!canEditTenantInfo || isSaving"
          ></textarea>

          <div v-if="canEditTenantInfo" class="about-actions">
            <button type="button" class="vp-btn vp-btn-ghost vp-btn-sm" :disabled="isSaving" @click="cancelEditing">
              {{ $t('common.cancel') }}
            </button>
            <button type="button" class="vp-btn vp-btn-primary vp-btn-sm" :disabled="isSaving" @click="saveAbout">
              <span v-if="isSaving" class="spinner-border spinner-border-sm"></span>
              {{ $t('common.save') }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import tenantInfoService from '@/services/tenantInfoService';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'ProfileAboutCard',
  props: {
    about: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['updated'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    
    // Permission check
    const canEditTenantInfo = computed(() => hasPermission('Supplier.TenantInfo.Set') && !props.isReadOnly);
    
    const editing = ref(false);
    const editAbout = ref(props.about || '');
    const isSaving = ref(false);

    const startEditing = () => { 
      editing.value = true; 
      editAbout.value = props.about || '';
    };
    
    const cancelEditing = () => { 
      editing.value = false; 
      editAbout.value = props.about || '';
    };
    
    const saveAbout = async () => {
      isSaving.value = true;
      try {
        // Update only the about property via tenant extra properties
        await tenantInfoService.setExtraProperty('About', editAbout.value);
        
        editing.value = false;
        
        // Emit updated event to parent to refresh data
        emit('updated');
        
        store.dispatch('notify', {
          message: t('profile.about.updateSuccess'),
          type: 'success'
        });
      } catch (error) {
        console.error('Error updating about:', error);
        store.dispatch('notify', {
          message: t('profile.about.updateFailed'),
          type: 'error'
        });
      } finally {
        isSaving.value = false;
      }
    };

    const formatAbout = (about) => {
      // Implement your formatting logic here
      return about;
    };

    return {
      editing,
      editAbout,
      isSaving,
      startEditing,
      cancelEditing,
      saveAbout,
      formatAbout,
      t,
      canEditTenantInfo
    };
  }
};
</script> 

<style scoped>
.about-text {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--vp-body-color);
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.about-text--empty {
  color: var(--vp-gray-500);
}

.about-textarea {
  resize: vertical;
}

.about-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}
</style>
